/*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
* Copyright 2023 Adobe
* All Rights Reserved.
*
* NOTICE: All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/

"use client";
import { Flex, Button, View, Text, ActionButton } from "@adobe/react-spectrum";
import Image from "next/image";
import { useEffect, useState, useRef } from "react";
import ChevronRight from "@spectrum-icons/workflow/ChevronRight";
import ChevronLeft from "@spectrum-icons/workflow/ChevronLeft";
import style from './image-panel.module.scss'
import Link from "next/link";
import classNames from "classnames";
import { sendAdobeAnalyticsAssetClickedEvent, sendAdobeAnalyticsReferrer } from '@/utils/utils';
import Loading from "@/app/[lng]/(module)/search/loading";
import { useTranslations } from "next-intl";
import { useRouter } from 'next/navigation'

export default function ImagePanel({ data, imageData, query, locale, similarId, id, desc }) {
  const [url, setUrl] = useState("");
  const [nextId, setNextId] = useState("");
  const [prevId, setPrevId] = useState("");
  const hasExecuted = useRef(false);
  const [error, setError] = useState(false);
  const t = useTranslations("");
  const urls = `https://${process.env.NEXT_PUBLIC_cpApiHost}/${process.env.NEXT_PUBLIC_dcx_path}/${similarId ? similarId : id}/rendition/images/initial/version/0/format/jpg/dimension/width/size/400`;
  const router = useRouter()

  useEffect(()=>{
    sendAdobeAnalyticsReferrer();
  },[])

  useEffect(() => {
    if (!hasExecuted.current) {
      sendAdobeAnalyticsAssetClickedEvent(id);
      hasExecuted.current = true;
    }
    setUrl(data?._links?.rendition?.href.replace('{format}', 'jpg').replace('{dimension}', 'width').replace('{size}', '1200'));

    if (data.id === similarId) {
      setPrevId(id);
      setNextId(imageData[0].id);
    }

    if (similarId === undefined) {
      setNextId(imageData[0].id);
      return;
    }

    const currentIndex = imageData.findIndex(item => item.id === similarId);


    if (currentIndex > 0 && currentIndex < imageData.length) {
      setNextId(imageData[currentIndex + 1]?.id);
      setPrevId(imageData[currentIndex - 1]?.id);
    } else if (currentIndex === 0) {
      setNextId(imageData[currentIndex + 1]?.id);
    } else setPrevId('')

  }, [data, imageData, similarId, id])

  if (!url || url.length === 0) return <Loading />


  const handleError = () => {
    setError(true);
  }

  const handleBackButtonPress = () => {
    router.back(); 
  }

  return (
    <Flex
      UNSAFE_className={style.main}
      justifyContent="center"
      position="relative"
      alignItems={{ base: 'start', L: 'center' }}
    >
      <View isHidden={{ L: false, base: true }}>
        <ActionButton 
          onPress={handleBackButtonPress} 
          zIndex={5} 
          position={'absolute'} 
          top={5} 
          left={5} 
          isQuiet 
          staticColor="black" 
          UNSAFE_className={style.backBtn}
        >
          <ChevronLeft size="S" /> 
          <Text> {t('back')} </Text>
        </ActionButton>
      </View>
      <div className={style.imageContainer}>
        {prevId && (
          <Link href={`/${locale}/discover/category/${query}/${id}${prevId === id ? '' : `/similar/${prevId}`}`}>
            <View>
              <Button
                variant="secondary"
                UNSAFE_className={classNames(style.arrowIcon)}
                aria-label="left-arrow"
                isHidden={{ base: true, L: false }}
              >
                <ChevronLeft />
              </Button>
            </View>
          </Link>
        )}

        <div className={style.innerContainer} onContextMenu={e => e.preventDefault()}>
          <Image width={800} height={800} src={url} alt={desc} className={style.mainImg} />
          <div className={style.beforediv} style={{ display: error && "none" }}>
            <Image onError={handleError} width={800} height={800} src={urls} alt={desc} className={style.beforeImg} />
          </div>
        </div>

        {nextId && (
          <Link href={`/${locale}/discover/category/${query}/${id}/similar/${nextId}`}>
            <View>
              <Button
                variant="secondary"
                UNSAFE_className={classNames(style.arrowIcon)}
                aria-label="right-arrow"
                isHidden={{ base: true, L: false }}
              >
                <ChevronRight />
              </Button>
            </View>
          </Link>
        )}
      </div>
    </Flex>
  );
}